// frontend/src/components/conversion/ConversionPopupGate.js
//
// THE EAGER HALF of the conversion popup - the only part every public page
// pays for. It decides WHETHER and WHEN; the card itself is a lazy chunk that
// is fetched only when the trigger fires (or prefetched once the page is idle
// and every condition already holds). Step 2 - the sign-up doors and Google's
// script - is a second lazy chunk the card loads only on "Start now".
//
// Kept tiny on purpose: no MUI, no sign-up hook, no copy. Everything it
// imports already rides in the eager bundle.
//
// WHO SEES IT - every condition must hold, and an unknown fails closed:
//   - not a prerender snapshot (checked BEFORE any dynamic import(), or webpack
//     would inject the chunk's <script> into the saved static HTML);
//   - an allowlisted route (conversionPopupPages.js);
//   - signed out: auth finished loading, not authenticated, and no "probably
//     signed in" hint cookie;
//   - organic: not a Google Ads visitor (owner D12 - they get the hero test);
//   - the Hebrew site (there is no approved English copy);
//   - the cookie banner already answered (re-evaluated on CONSENT_SAVED_EVENT);
//   - the frequency rules (once per session, 7 quiet days after a close).
// Then the trigger must fire - 25s of visible time, OR 35% scroll once at
// least 10s of visible time has passed, whichever comes first
// (useConversionTrigger.js) - and the moment must be free: no other dialog
// open, the visitor not typing elsewhere - otherwise it waits for the moment
// to clear.
//
// THE ROUTE comes from `window.location.pathname`, read on every render - not
// from a router hook. This module rides in every public page's AuthLayout,
// and importing the router here would make every page test that renders
// AuthLayout load it (react-router-dom does not resolve under CRA's jest at
// all, and react-router needs browser globals jsdom lacks). Reading the
// address is exact for this use: React Router updates it before it renders,
// and a route change re-renders the routed page and with it this AuthLayout
// child (a different page remounts the layout outright).
//
// ONCE SHOWN IT STAYS until the visitor closes it. Auth flipping mid-sign-up
// (Google signs the user in before the hook navigates) must not unmount the
// card under the hook, and neither may a language toggle unmount a card the
// visitor is typing in, so the conditions above gate SHOWING only. Between
// "fired" and "open" the language turning English drops this mount's show
// ("done": no event, no quiet period), and a route change starts the wait
// over on the new page.

import React, {
  Component,
  Suspense,
  lazy,
  useCallback,
  useEffect,
  useRef,
  useState,
} from "react";

import { useAuth } from "../../contexts/AuthContext";
import { isPaidAdVisitor } from "../../utils/adVisitor";
import {
  CONSENT_SAVED_EVENT,
  isConsentRequired,
} from "../CookieConsent/consentUtils";
import { resolvePopupPage } from "./conversionPopupPages";
import { frequencyAllowsShow } from "./conversionPopupMemory";
import { isMomentBlocked } from "./conversionPopupBlockers";
import useConversionTrigger from "./useConversionTrigger";

/** How often a blocked moment (open dialog, typing) is re-checked. */
export const BLOCKED_RECHECK_MS = 1500;

/** Step 1's chunk. Called early to prefetch; React.lazy reuses the same module. */
export const loadConversionPopupCard = () => import("./ConversionPopupCard");
const ConversionPopupCard = lazy(loadConversionPopupCard);

/** The current pathname, or "" when there is no readable location. */
function readPathname() {
  try {
    return window.location.pathname || "";
  } catch {
    return "";
  }
}

/** Is this render a prerender snapshot (scripts/prerender.js)? */
function isPrerenderSnapshot() {
  try {
    return typeof window === "undefined" || window.__PRERENDER__ === true;
  } catch {
    return true;
  }
}

/**
 * The auth state, or null outside an AuthProvider. A gate must never take a
 * page down: `useAuth` throws without its provider (some page tests render
 * AuthLayout bare), and "unknown" is simply "do not show".
 */
function useOptionalAuth() {
  try {
    return useAuth();
  } catch {
    return null;
  }
}

/**
 * Signed out, PROVEN: auth finished loading, not authenticated, no hint. Strict
 * booleans, so a partial auth value (a test double, a provider still booting)
 * fails closed.
 */
function isProvenSignedOut(auth) {
  return Boolean(
    auth && auth.loading === false && auth.isAuthenticated === false && !auth.authHint
  );
}

/** The visitor's answer to the cookie banner, kept live. */
function useConsentPending() {
  const [pending, setPending] = useState(() => isConsentRequired());
  useEffect(() => {
    const onSaved = () => setPending(isConsentRequired());
    window.addEventListener(CONSENT_SAVED_EVENT, onSaved);
    return () => window.removeEventListener(CONSENT_SAVED_EVENT, onSaved);
  }, []);
  return pending;
}

/** Renders nothing if the lazy card (or anything in it) fails. */
class PopupErrorBoundary extends Component {
  constructor(props) {
    super(props);
    this.state = { failed: false };
  }

  static getDerivedStateFromError() {
    return { failed: true };
  }

  componentDidCatch() {
    // Deliberately silent: a failed popup is a non-event for the page, and
    // nothing here may log what the visitor typed.
  }

  render() {
    return this.state.failed ? null : this.props.children;
  }
}

/**
 * After window load, when the browser is idle, fetch step 1's chunk so the
 * card opens without a network wait. Only while every gate condition holds.
 */
function usePrefetchWhenIdle(enabled) {
  useEffect(() => {
    if (!enabled) return undefined;
    let idleId = null;
    let timeoutId = null;
    const prefetch = () => {
      loadConversionPopupCard().catch(() => {
        /* the real load retries when the trigger fires */
      });
    };
    const schedule = () => {
      if (typeof window.requestIdleCallback === "function") {
        idleId = window.requestIdleCallback(prefetch, { timeout: 5000 });
      } else {
        timeoutId = setTimeout(prefetch, 2000);
      }
    };
    if (document.readyState === "complete") schedule();
    else window.addEventListener("load", schedule, { once: true });
    return () => {
      window.removeEventListener("load", schedule);
      if (idleId !== null && typeof window.cancelIdleCallback === "function") {
        window.cancelIdleCallback(idleId);
      }
      if (timeoutId !== null) clearTimeout(timeoutId);
    };
  }, [enabled]);
}

/**
 * The armed gate (never in a snapshot).
 *
 * @param {object}  props
 * @param {boolean} props.isHebrew - AuthLayout's live site language
 */
function ArmedPopupGate({ isHebrew }) {
  const pathname = readPathname();
  const auth = useOptionalAuth();
  const consentPending = useConsentPending();
  const page = resolvePopupPage(pathname);

  // "fired": the trigger went off and the card waits for a free moment.
  // "open": the card is on screen. "done": closed - never again on this mount.
  const [phase, setPhase] = useState("idle");
  // The page the card opened on, frozen: page_type and the pre-fill must not
  // change under an open card.
  const [openedPage, setOpenedPage] = useState(null);

  const eligible =
    phase === "idle" &&
    page !== null &&
    isHebrew === true &&
    isProvenSignedOut(auth) &&
    !consentPending &&
    !isPaidAdVisitor() &&
    frequencyAllowsShow();

  // The page the trigger fired on. A deferred show (a blocked moment) re-checks
  // it: the trigger was earned on THAT page, never the next one.
  const firedPathRef = useRef(null);
  const onFire = useCallback(() => {
    firedPathRef.current = readPathname();
    setPhase("fired");
  }, []);
  useConversionTrigger({ enabled: eligible, resetKey: pathname, onFire });
  usePrefetchWhenIdle(eligible);

  // A route change before the card opened starts the wait over (new page).
  useEffect(() => {
    setPhase((current) => (current === "fired" ? "idle" : current));
  }, [pathname]);

  // Fired: show at the first free moment, re-checking the conditions then -
  // another tab may have started the quiet period meanwhile.
  useEffect(() => {
    if (phase !== "fired") return undefined;
    let timer = null;
    const attempt = () => {
      timer = null;
      // The language gates eligibility only: English now means no show on
      // this mount at all - not an idle that would re-arm on the way back.
      if (isHebrew !== true) {
        setPhase("done");
        return;
      }
      // Navigated since the trigger fired (read live - a timer re-check may
      // run before the layout re-renders): start over on the new page.
      if (readPathname() !== firedPathRef.current) {
        setPhase("idle");
        return;
      }
      const stillEligible =
        page !== null &&
        isProvenSignedOut(auth) &&
        !consentPending &&
        frequencyAllowsShow();
      if (!stillEligible) {
        setPhase("idle");
        return;
      }
      if (isMomentBlocked()) {
        timer = setTimeout(attempt, BLOCKED_RECHECK_MS);
        return;
      }
      setOpenedPage(page);
      setPhase("open");
    };
    attempt();
    return () => {
      if (timer !== null) clearTimeout(timer);
    };
    // `page` is derived from pathname; the route-change effect above resets.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [phase, auth, consentPending, pathname, isHebrew]);

  const handleClose = useCallback(() => setPhase("done"), []);

  // Not gated on `isHebrew` here: an open card stays whatever the language.
  if (phase !== "open" || !openedPage) return null;

  return (
    <PopupErrorBoundary>
      <Suspense fallback={null}>
        <ConversionPopupCard
          pageType={openedPage.pageType}
          professionSlug={openedPage.professionSlug}
          onClose={handleClose}
        />
      </Suspense>
    </PopupErrorBoundary>
  );
}

/**
 * The conversion popup, as AuthLayout mounts it on public pages.
 *
 * @param {object}  props
 * @param {boolean} props.isHebrew
 */
export default function ConversionPopupGate({ isHebrew }) {
  if (isPrerenderSnapshot()) return null;
  return <ArmedPopupGate isHebrew={isHebrew} />;
}
